import { Component, Input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { DeviceService } from '../../../device.service';
import { Subscription } from 'rxjs';
import { CommonService } from 'src/app/shared/services/common.service';
import { sum } from 'lodash-es';
import { SiteDashboardService } from 'src/app/home/site-dashboard/site-dashboard.service';
import { UsersService } from 'src/app/users/users.service';
import { DeviceFactory } from '../../../device-factory/device-factory';
import { mainDeviceConfig } from '../../../device-factory/device-config';

@Component({
	selector: 'app-im-usage-data',
	templateUrl: './im-usage-data.component.html',
	styleUrls: []
})
export class IMUsageDataComponent {

	@Input() device: any = {}

	maxDate = new Date((new Date()).getTime() - 24 * 60 * 60 * 1000); // yesterday
	toDate = this.maxDate; // yesterday
	fromDate = new Date((new Date()).getTime() - 30 * 24 * 60 * 60 * 1000); // 30 days ago
	minDate = this.fromDate;

	getDailyDetails: Subscription = new Subscription();
	permissionsSub: Subscription = new Subscription();

	usageDataExport: any = [];
	usageDataChart: any = [];
	usageDataIsEmpty: boolean = true;
	usageData: any
	
	deviceConfig: mainDeviceConfig | null = null;

	constructor(
		private translate: TranslateService,
		private deviceService: DeviceService,
		private commonService: CommonService,
		private siteDashboardService: SiteDashboardService,
		private usersService: UsersService,
	) {}

	ngOnInit() {
		if (this.device?.config_info?.installation_date)
			this.minDate = moment.utc(this.device.config_info.installation_date * 1000).startOf('day').toDate();
	}
	
	ngOnChanges(changes) {
		if(changes.device && this.device) {
			this.deviceConfig = DeviceFactory.createDevice(changes.device.currentValue.config_info.sct_device_type).config;
			this.getUsageData();
		}
	}

	changeDate(field, value) {
		this[field] = value;
		this.getUsageData();
	}

	getUsageData() {
		const zoneDiff	= new Date().getTimezoneOffset() * -1;
		let fromDate:any	= new Date(new Date(this.fromDate).getTime() + (zoneDiff * 60 * 1000));
		let toDate:any = new Date(new Date(this.toDate).getTime() + (zoneDiff * 60 * 1000));
		fromDate = moment(fromDate).utc().startOf('day').unix();
		toDate = moment(toDate).utc().endOf('day').unix();

		const options = { checkConnectivityAfterShippingDate: true };
		this.getDailyDetails = this.deviceService.getDailyDetails(this.device.mac_address, fromDate, toDate, options).subscribe((data: any) => {
			this.usageDataExport = [];
			let billedVal = 0,
				billedAvg = 0,
				consumedUsageTimeVal = 0,
				generatedUsageTimeVal = 0,
				peakPowerVal = 0;

			for (const dayUnix in data) {
				const dayData = data[dayUnix] || {};

				if (!Object.keys(dayData).length)
					continue;

				this.usageDataExport.push([
					moment.utc(+dayData.date * 1000).format('l'),
					dayData.event_consumed_energy,
					dayData.consumed_energy_duration,
					dayData.generated_energy_duration,
					dayData.negative_peak_power,
				]);

				billedVal += dayData.event_consumed_energy;
				consumedUsageTimeVal += dayData.consumed_energy_duration;
				generatedUsageTimeVal += dayData.generated_energy_duration;
				peakPowerVal = Math.max(dayData.negative_peak_power, peakPowerVal);
			}

			if (Object.keys(data).length)
				billedAvg = billedVal / Object.keys(data).length;

			this.usageDataChart = [
				{ label: this.translate.instant('iotemeter.performance_analytics.total_billed_energy'), unit: 'kWh' },
				{ label: this.translate.instant('iotemeter.performance_analytics.consumed_total_usage_time'), unit: '' },
				{ label: this.translate.instant('iotemeter.performance_analytics.generated_total_usage_time'), unit: '' },
				{ label: this.translate.instant('iotemeter.performance_analytics.daily_billed_energy'), unit: 'kWh' },
				{ label: this.translate.instant('iotemeter.performance_analytics.peak_power'), unit: 'kW' }
			];

			const consumedDurationFormatted = this.commonService.convertSecondsToHoursMinsSecs(+consumedUsageTimeVal);
			const generatedDurationFormatted = this.commonService.convertSecondsToHoursMinsSecs(+generatedUsageTimeVal);
			const dataTemp = [+(+billedVal).toFixed(2), consumedDurationFormatted, generatedDurationFormatted, +(+billedAvg).toFixed(2), +(+peakPowerVal).toFixed(2)];

			this.usageDataIsEmpty = sum([+(+billedVal).toFixed(2), +(+billedAvg).toFixed(2), +(+consumedUsageTimeVal).toFixed(2), +(+generatedUsageTimeVal).toFixed(2), +(+peakPowerVal).toFixed(2)]) === 0;

			this.usageDataIsEmpty = false;

			this.usageData = dataTemp.map((value, i) => {
				return { label: this.usageDataChart[i].label, value: value, unit: this.usageDataChart[i].unit };
			});

			this.usageData[1] = [
				this.usageData[1], this.usageData[2]
			];

			delete this.usageData[2];
			this.usageData = Object.values(this.usageData);
		});
	}

	exportUsageData() {
		let columnNames = [
			this.translate.instant('g.date'),
			this.translate.instant('iotemeter.performance_analytics.billed_kwhrs'),
			this.translate.instant('iotemeter.performance_analytics.consumed_total_usage_time'),
			this.translate.instant('iotemeter.performance_analytics.generated_total_usage_time'),
			this.translate.instant('iotemeter.performance_analytics.peak_power')
		];

		const fileName = this.translate.instant('iotemeter.performance_analytics.usage_data');

		this.permissionsSub = this.siteDashboardService.permissionsOfCurrentSite.subscribe(data => {
			const enterprisePermissions = data;
			if (!this.usersService.hasAccessPermission(enterprisePermissions, 'debug_data', 'read', null, this.deviceConfig.permissionMapperKey)) {
				columnNames = columnNames.filter(column => column != this.translate.instant('iotemeter.performance_analytics.billed_kwhrs'));
				this.usageDataExport.map((row: any) => row.splice(1, 1)); // Index 1 is the Consumed Energy (kWh) index.
			}
		});

		this.commonService.exportToCsv(columnNames, this.usageDataExport, fileName);
	}

	ngOnDestroy() {
		this.getDailyDetails?.unsubscribe();
		this.permissionsSub?.unsubscribe();
	}
}
